import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  LinearProgress
} from '@material-ui/core';
import { FaInfoCircle } from 'react-icons/fa';
import { ethers } from 'ethers';
import { useAuth } from './auth/AuthContext';
import { 
  metamaskLogo, 
  futLogoIcon200,
  E3D_TOKEN_ADDRESS,
  E3D_NFT_MANAGER_ADDRESS,
  E3D_TOKEN_ABI,
  E3D_NFT_MANAGER_ABI
} from './config';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#121212',
    color: '#ffffff'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  appLogo: {
    height: 40,
    marginRight: theme.spacing(2)
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    color: '#ffffff'
  },
  stakeCard: {
    backgroundColor: '#1f2026',
    borderRadius: 16,
    maxWidth: 450,
    width: '100%',
    color: '#ffffff',
    border: '1px solid #4d79ff',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid rgba(77, 121, 255, 0.4)'
  },
  cardContent: {
    padding: theme.spacing(2)
  },
  walletButton: {
    borderRadius: '20px',
    borderColor: 'white',
    borderWidth: '2px',
    borderStyle: 'solid',
    backgroundColor: '#333',
    color: '#fff',
    marginRight: '20px',
    height: '40px',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#444'
    }
  },
  walletIcon: {
    height: '20px',
    marginRight: '8px'
  },
  tokenContainer: {
    backgroundColor: '#252525',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  inputRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  amountInput: {
    '& .MuiInputBase-input': {
      color: '#ffffff',
      textAlign: 'right',
      fontSize: '24px',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'transparent'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'transparent'
    }
  },
  tokenInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  tokenIconContainer: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    backgroundColor: '#333',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  tokenIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain'
  },
  tokenSymbol: {
    fontWeight: 'bold',
    color: '#ffffff'
  },
  stakeInfo: {
    marginTop: theme.spacing(2),
    backgroundColor: '#252525',
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    '& .MuiTypography-body2': {
      color: '#cccccc'
    }
  },
  btnStake: {
    backgroundColor: '#4C6FFF',
    color: 'white',
    fontWeight: 'bold',
    borderRadius: 16,
    padding: '12px 0',
    marginTop: theme.spacing(2),
    width: '100%',
    '&:hover': {
      backgroundColor: '#3A5AE0',
    },
    '&.Mui-disabled': {
      backgroundColor: '#2A3A80',
      color: '#999'
    }
  },
  btnUnstake: {
    backgroundColor: '#D32F2F',
    color: 'white',
    fontWeight: 'bold',
    borderRadius: 16,
    padding: '12px 0',
    marginTop: theme.spacing(1),
    width: '100%',
    '&:hover': {
      backgroundColor: '#B71C1C',
    },
    '&.Mui-disabled': {
      backgroundColor: '#7F1818',
      color: '#999'
    }
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: '#ffffff'
  },
  settingsButton: {
    color: '#cccccc',
  },
  benefitContainer: {
    backgroundColor: '#292933',
    borderRadius: 15,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: '1px solid #4d79ff',
  },
  benefitTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#4C6FFF'
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#4C6FFF'
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    padding: theme.spacing(2),
  }
}));

const StakePage = ({ 
  open = null, 
  onClose = null,
  isDialog = open !== null
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  
  // Use the unified wallet connection state from AuthContext
  const { userAddress, isWalletConnected, ethersProvider, connectWallet, disconnectWallet } = useAuth();
  
  // Local state variables
  // ETH balance no longer needed as we focus only on E3D tokens
  const [tokenBalance, setTokenBalance] = useState('0.00');
  const [stakedAmount, setStakedAmount] = useState('0.00');
  const [inputAmount, setInputAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [transactionSuccess, setTransactionSuccess] = useState(false);
  const [transactionType, setTransactionType] = useState('');
  const [stakingRate, setStakingRate] = useState(1); // Initialize with contract's default value of 1%

  // Handle wallet connection
  const handleConnectWallet = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      console.log('StakePage: Requesting wallet connection');

      await connectWallet();
      console.log('StakePage: Wallet connected successfully');

      setIsLoading(false);
    } catch (error) {
      console.error('StakePage: Failed to connect wallet:', error);
      setErrorMessage('Failed to connect wallet: ' + (error.message || 'Unknown error'));
      setIsLoading(false);
    }
  };

  // Handle wallet disconnection
  const handleDisconnectWallet = () => {
    console.log('StakePage: Disconnecting wallet');
    disconnectWallet();
    setTokenBalance('0.00');
    setStakedAmount('0.00');
    setInputAmount('');
    setTransactionSuccess(false);
    setErrorMessage('');
  };

  // Update balances when wallet or provider changes
  useEffect(() => {
    // Update balance when wallet is connected
    if (ethersProvider && userAddress) {
      loadUserInfo();
    }
  }, [ethersProvider, userAddress]);

  // Load user's token balance and staking info
  const loadUserInfo = async () => {
    if (!ethersProvider || !userAddress) return;

    try {
      setIsLoading(true);
      setErrorMessage('');

      // Create contract instances
      const tokenContract = new ethers.Contract(E3D_TOKEN_ADDRESS, E3D_TOKEN_ABI, ethersProvider);

      const nftManagerContract = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, E3D_NFT_MANAGER_ABI, ethersProvider);

      // Get token balance
      const balance = await tokenContract.balanceOf(userAddress);
      const formattedBalance = ethers.utils.formatUnits(balance, 18);
      setTokenBalance(parseFloat(formattedBalance).toFixed(2));

      // Get staked tokens amount
      const staked = await nftManagerContract.stakedE3D(userAddress);
      const formattedStaked = ethers.utils.formatUnits(staked, 18);
      setStakedAmount(parseFloat(formattedStaked).toFixed(2));

      // Get staking APR
      const stakingRate = await nftManagerContract.rewardRateStaking();
      setStakingRate(stakingRate.toNumber());

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading user info:', error);
      setErrorMessage('Failed to load account info: ' + (error.message || 'Unknown error'));
      setIsLoading(false);
    }
  };

  // Handle staking tokens
  const handleStake = async () => {
    if (!ethersProvider || !userAddress || !inputAmount) {
      setErrorMessage('Please connect your wallet and enter an amount to stake');
      return;
    }

    if (parseFloat(inputAmount) <= 0 || isNaN(parseFloat(inputAmount))) {
      setErrorMessage('Please enter a valid amount');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      setTransactionSuccess(false);
      setTransactionType('');

      // Create contract instance with signer
      const signer = ethersProvider.getSigner();
      const nftManagerContract = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, E3D_NFT_MANAGER_ABI, signer);

      // Amount to stake in wei
      const amountToStake = ethers.utils.parseUnits(inputAmount, 18);

      // Execute the stake transaction
      console.log(`Staking ${inputAmount} E3D tokens`);
      setErrorMessage('Now staking... Please confirm the transaction in your wallet');

      try {
        // Set gas limit explicitly to help with transaction execution
        const gasEstimate = await nftManagerContract.estimateGas.stake(amountToStake);
        console.log('Gas estimate for staking:', gasEstimate.toString());

        const stakeTx = await nftManagerContract.stake(amountToStake, {
          gasLimit: gasEstimate.mul(120).div(100), // Add 20% buffer to gas estimate
        });
        console.log('Stake transaction hash:', stakeTx.hash);
        setErrorMessage('Staking transaction submitted. Waiting for confirmation...');

        const stakeReceipt = await stakeTx.wait();
      } catch (stakeError) {
        console.error('Staking transaction failed:', stakeError);
        setErrorMessage('Failed to stake tokens: ' + (stakeError.message || 'Unknown error'));
        setIsLoading(false);
        return;
      }
      console.log('Staking successful!');

      // Update balances
      // Get token balance after staking
      const tokenContract = new ethers.Contract(E3D_TOKEN_ADDRESS, E3D_TOKEN_ABI, ethersProvider);
      const balance = await tokenContract.balanceOf(userAddress);
      const formattedBalance = ethers.utils.formatUnits(balance, 18);
      setTokenBalance(parseFloat(formattedBalance).toFixed(2));

      // Get staked tokens amount
      const stakedAmount = await nftManagerContract.stakedE3D(userAddress);
      const formattedStakedAmount = parseFloat(ethers.utils.formatUnits(stakedAmount, 18)).toFixed(2);
      setStakedAmount(formattedStakedAmount);

      setTransactionSuccess(true);
      setTransactionType('stake');
      setInputAmount('');
      setErrorMessage('');
    } catch (error) {
      console.error('Error staking tokens:', error);

      // Handle user rejection without showing error
      if (
        error.code === 4001 || // MetaMask error code for user rejection
        error.code === 'ACTION_REJECTED' ||
        (error.code && error.code.toString().includes('ACTION_REJECTED')) ||
        (error.message && error.message.includes('user rejected')) ||
        (error.message && error.message.includes('User denied')) ||
        (error.message && error.message.includes('User rejected'))
      ) {
        console.log('User rejected the transaction - no error displayed');
      } else {
        setErrorMessage('Error staking tokens: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle unstaking tokens
  const handleUnstake = async () => {
    if (!ethersProvider || !userAddress) {
      setErrorMessage('Please connect your wallet');
      return;
    }

    if (parseFloat(inputAmount) <= 0 || isNaN(parseFloat(inputAmount))) {
      setErrorMessage('Please enter a valid amount');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      setTransactionSuccess(false);
      setTransactionType('');

      // Create contract instance with signer
      const signer = ethersProvider.getSigner();
      const nftManagerContract = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, E3D_NFT_MANAGER_ABI, signer);

      // Amount to unstake in wei
      const amountToUnstake = ethers.utils.parseUnits(inputAmount, 18);

      // Execute the unstake transaction
      console.log(`Unstaking ${inputAmount} E3D tokens`);
      setErrorMessage('Unstaking... Please confirm the transaction in your wallet');

      try {
        // Set gas limit explicitly to help with transaction execution
        const gasEstimate = await nftManagerContract.estimateGas.withdrawStake(amountToUnstake);
        console.log('Gas estimate for unstaking:', gasEstimate.toString());

        const unstakeTx = await nftManagerContract.withdrawStake(amountToUnstake, {
          gasLimit: gasEstimate.mul(120).div(100), // Add 20% buffer to gas estimate
        });
        console.log('Unstake transaction hash:', unstakeTx.hash);
        setErrorMessage('Unstaking transaction submitted. Waiting for confirmation...');

        const unstakeReceipt = await unstakeTx.wait();
      } catch (unstakeError) {
        console.error('Unstaking transaction failed:', unstakeError);
        setErrorMessage('Failed to unstake tokens: ' + (unstakeError.message || 'Unknown error'));
        setIsLoading(false);
        return;
      }
      console.log('Unstaking successful!');

      // Update balances
      // Get token balance after unstaking
      const tokenContract = new ethers.Contract(E3D_TOKEN_ADDRESS, E3D_TOKEN_ABI, ethersProvider);
      const balance = await tokenContract.balanceOf(userAddress);
      const formattedBalance = ethers.utils.formatUnits(balance, 18);
      setTokenBalance(parseFloat(formattedBalance).toFixed(2));

      // Get staked tokens amount
      const stakedAmount = await nftManagerContract.stakedE3D(userAddress);
      const formattedStakedAmount = parseFloat(ethers.utils.formatUnits(stakedAmount, 18)).toFixed(2);
      setStakedAmount(formattedStakedAmount);

      setTransactionSuccess(true);
      setTransactionType('unstake');
      setInputAmount('');
      setErrorMessage('');
    } catch (error) {
      console.error('Error unstaking tokens:', error);

      // Handle user rejection without showing error
      if (
        error.code === 4001 || // MetaMask error code for user rejection
        error.code === 'ACTION_REJECTED' ||
        (error.code && error.code.toString().includes('ACTION_REJECTED')) ||
        (error.message && error.message.includes('user rejected')) ||
        (error.message && error.message.includes('User denied')) ||
        (error.message && error.message.includes('User rejected'))
      ) {
        console.log('User rejected the transaction - no error displayed');
      } else {
        setErrorMessage('Error unstaking tokens: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate staking benefits based on amount
  const calculateBenefits = (amount) => {
    const parsedAmount = parseFloat(amount);

    let mintDiscount = 0;
    let royaltyRate = 10; // Default 10%

    if (parsedAmount >= 1000) {
      mintDiscount = 30;
      royaltyRate = 5;
    } else if (parsedAmount >= 500) {
      mintDiscount = 20;
      royaltyRate = 7;
    } else if (parsedAmount >= 100) {
      mintDiscount = 10;
      royaltyRate = 10;
    }

    return { mintDiscount, royaltyRate };
  };

  // Current benefits based on staked amount
  const currentBenefits = calculateBenefits(stakedAmount);

  // Helper to shorten wallet address for display
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Content of the stake page/dialog
  const stakeContent = (
    <div className={classes.root} style={{ padding: '0', display: 'flex', flexDirection: 'column' }}>
      {/* Wallet Button centered with less vertical space */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
        {!userAddress ? (
          <Button
            className={classes.walletButton}
            onClick={handleConnectWallet}
            disabled={isLoading}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {isLoading ? 'Connecting...' : 'Connect Wallet'}
          </Button>
        ) : (
          <Button
            className={classes.walletButton}
            onClick={handleDisconnectWallet}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {shortenAddress(userAddress)}
          </Button>
        )}
      </div>

      <Card className={classes.stakeCard}>
        <CardContent className={classes.cardContent}>
          {/* Token Input */}
          <div className={classes.tokenContainer}>
            <div className={classes.inputRow}>
              <div className={classes.tokenInfo}>
                <div className={classes.tokenIconContainer}>
                  <img src={futLogoIcon200} alt="E3D" className={classes.tokenIcon} />
                </div>
                <Typography variant="h6" className={classes.tokenSymbol}>
                  E3D
                </Typography>
              </div>
              <TextField
                className={classes.amountInput}
                value={inputAmount}
                onChange={(e) => setInputAmount(e.target.value)}
                placeholder="0.00"
                type="number"
                InputProps={{
                  inputProps: {
                    min: "0",
                    step: "1",
                  },
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Typography variant="body2" style={{ color: '#ffffff' }}>
                Wallet Balance: {tokenBalance} E3D
              </Typography>
              <Typography 
                variant="body2" 
                style={{ color: '#4C6FFF', cursor: 'pointer' }}
                onClick={() => setInputAmount(tokenBalance)}
              >
                Max
              </Typography>
            </div>
          </div>
          
          {/* Staked Amount */}
          <div className={classes.stakeInfo}>
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Currently Staked
                <Tooltip title="Amount of E3D tokens currently staked">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {stakedAmount} E3D
              </Typography>
            </div>
            
            {stakingRate > 0 && (
              <div className={classes.infoRow}>
                <Typography variant="body2">
                  Staking APR
                  <Tooltip title="Annual Percentage Rate for staking rewards">
                    <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                      <FaInfoCircle size={12} />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Typography variant="body2">
                  {stakingRate}%
                </Typography>
              </div>
            )}
            
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Minting Discount
                <Tooltip title="Discount on NFT minting fees">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {currentBenefits.mintDiscount}%
              </Typography>
            </div>
            
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Royalty Rate
                <Tooltip title="Royalty rate applied to your NFTs">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {currentBenefits.royaltyRate}%
              </Typography>
            </div>
          </div>
          
          {/* Error Message */}
          {errorMessage && (
            <Typography 
              variant="body2" 
              style={{ color: '#f44336', marginTop: 8, textAlign: 'center' }}
            >
              {errorMessage}
            </Typography>
          )}
          
          {/* Success Message */}
          {transactionSuccess && (
            <Typography 
              variant="body2" 
              style={{ color: '#4caf50', marginTop: 8, textAlign: 'center' }}
            >
              {transactionType === 'stake' 
                ? 'Tokens staked successfully!' 
                : 'Tokens unstaked successfully!'}
            </Typography>
          )}
          
          {/* Loading Indicator */}
          {isLoading && (
            <div style={{ marginTop: 16 }}>
              <LinearProgress style={{ backgroundColor: '#333', color: '#4C6FFF' }} />
              <Typography 
                variant="body2" 
                style={{ color: '#ffffff', marginTop: 8, textAlign: 'center' }}
              >
                Processing transaction...
              </Typography>
            </div>
          )}
          
          {/* Action Buttons */}
          <Button
            className={classes.btnStake}
            onClick={handleStake}
            disabled={!userAddress || isLoading || !inputAmount || parseFloat(inputAmount) <= 0 || parseFloat(inputAmount) > parseFloat(tokenBalance)}
          >
            Stake E3D
          </Button>
          
          <Button
            className={classes.btnUnstake}
            onClick={handleUnstake}
            disabled={!userAddress || isLoading || parseFloat(stakedAmount) <= 0 || (inputAmount && (parseFloat(inputAmount) <= 0 || parseFloat(inputAmount) > parseFloat(stakedAmount)))}
          >
            Unstake E3D
          </Button>
          
          {/* Benefits Container */}
          <div className={classes.benefitContainer}>
            <Typography variant="h6" className={classes.benefitTitle}>
              Staking Benefits
            </Typography>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>100+ E3D:</strong> 10% discount on NFT minting
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>500+ E3D:</strong> 20% discount on NFT minting, 7% royalty rate (vs 10%)
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>1000+ E3D:</strong> 30% discount on NFT minting, 5% royalty rate
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>Any amount:</strong> {stakingRate}% APR staking rewards
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Debug what's happening with the dialog mode
  console.log('StakePage render state:', { open, isDialog, userAddress });
  
  // This effect is no longer needed as wallet management is handled by AuthContext

  // Don't add any global style overrides that might break the dialog structure

  // Conditional rendering based on whether it's used as a dialog or standalone page
  return isDialog ? (
    <Dialog
      open={open === null ? false : open}
      onClose={onClose}
      maxWidth="sm"
      scroll="body"
      fullWidth={false}
      style={{ zIndex: 10000 }}
      PaperProps={{
        style: {
          border: '1px solid #4d79ff',
          borderRadius: '15px',
          maxWidth: '450px',
          margin: '0 auto',
          boxSizing: 'border-box',
          backgroundColor: '#1f2026',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }
      }}
    >
      <DialogTitle style={{ backgroundColor: '#1f2026', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            E3D Token Staking
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ backgroundColor: '#1f2026', padding: 0, height: 'auto', overflow: 'visible' }}>
        {stakeContent}
      </DialogContent>
    </Dialog>
  ) : (
    // Standalone page view with more padding
    <div style={{ padding: 20 }}>
      {stakeContent}
    </div>
  );
};

export default StakePage;
